// Finance ▸ Health & cash — the forecast's assumptions, editable by Jake
// (admin) and saved with history (CEO 2026-10-09, unit 2). Stephanie (finance)
// sees the same panel read-only. Defaults: $15K draw at month end, biweekly
// Friday payroll counted from 6/12/2026, January bonus = last January's payout.
import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { api, ApiError, apiErrorMessage } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useAuth } from "@/auth/AuthProvider";
import { Panel, focusRing, money, shortDate, type BillCadence, type CashflowAssumptions, type PayrollCadence } from "./shared";

interface Version { id: number | null; savedAt: string | null; savedByName: string | null; note: string; assumptions: CashflowAssumptions }
interface HistoryRow { id: number; savedAt: string; savedByName: string | null; note: string; changes: string[] }
interface Payload { ready: boolean; current: Version; defaults: CashflowAssumptions; history: HistoryRow[] }

const CADENCES: Array<[PayrollCadence, string]> = [["weekly", "Weekly"], ["biweekly", "Every two weeks"], ["semimonthly", "Twice a month (15th + last day)"], ["monthly", "Monthly"]];
const BILL_CADENCES: Array<[BillCadence, string]> = [["weekly", "Weekly"], ["biweekly", "Every two weeks"], ["monthly", "Monthly"], ["quarterly", "Quarterly"], ["annual", "Yearly"], ["once", "Once"]];
const field = "mt-1 w-full rounded border border-rule bg-white px-3 py-2 text-label disabled:bg-paper disabled:text-ink";
const newId = () => Math.random().toString(36).slice(2, 10);
const num = (v: string) => (v.trim() === "" ? 0 : Number(v));

export function ForecastAssumptions({ weeks, onSaved }: { weeks: string[]; onSaved: () => void }) {
  const { user } = useAuth();
  const canEdit = user?.roles.includes("admin") ?? false;
  const [data, setData] = useState<Payload | null>(null);
  const [draft, setDraft] = useState<CashflowAssumptions | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api<Payload>("/api/finance/forecast-assumptions");
      setData(r);
      setDraft(r.current.assumptions);
    } catch (e) {
      setError(apiErrorMessage(e, "Couldn't load the assumptions."));
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  if (!data || !draft) {
    return <Panel title="Forecast assumptions" id="assumptions">{error ? <p role="alert" className="text-body text-risk">{error}</p> : <p className="text-body text-ink-muted">Loading…</p>}</Panel>;
  }
  const edit = canEdit && data.ready;
  const set = <K extends keyof CashflowAssumptions>(k: K, v: CashflowAssumptions[K]) => { setSaved(null); setDraft({ ...draft, [k]: v }); };
  const dirty = JSON.stringify(draft) !== JSON.stringify(data.current.assumptions);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await api("/api/finance/forecast-assumptions", { method: "PUT", body: JSON.stringify({ expectedId: data!.current.id, assumptions: draft, note }) });
      setNote("");
      setSaved("Saved. The forecast above now uses these.");
      await load();
      onSaved();
    } catch (e) {
      setError(apiErrorMessage(e, "Couldn't save."));
      if (e instanceof ApiError && e.status === 409) void load();
    } finally {
      setBusy(false);
    }
  }

  const source = data.current.id
    ? `Saved by ${data.current.savedByName ?? "someone"}, ${shortDate(data.current.savedAt)}`
    : "Using the defaults — nothing saved yet";
  return (
    <Panel title="Forecast assumptions" id="assumptions" aside={source}>
      {!data.ready ? (
        <p className="mb-4 rounded border border-rule bg-paper px-4 py-3 text-body"><b>Preview.</b> Saving switches on once the forecast table is set up on the server (one command Jake runs). The forecast uses these defaults until then.</p>
      ) : !canEdit ? (
        <p className="mb-4 text-body text-ink-muted">Jake sets these. You can see what the forecast uses and every change made.</p>
      ) : null}

      <fieldset disabled={!edit} className="grid gap-4 md:grid-cols-3">
        <legend className="sr-only">Owner draw and payroll</legend>
        <label className="block text-meta font-semibold">Owner draw, per month (paid at month end)
          <input type="number" min={0} step={500} inputMode="decimal" value={draft.ownerDrawMonthly} onChange={(e) => set("ownerDrawMonthly", num(e.target.value))} className={cn(field, focusRing)} />
        </label>
        <label className="block text-meta font-semibold">Payroll cadence
          <select value={draft.payrollCadence} onChange={(e) => set("payrollCadence", e.target.value as PayrollCadence)} className={cn(field, "min-h-[40px]", focusRing)}>
            {CADENCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label className="block text-meta font-semibold">Payroll counted from (a payday)
          <input type="date" value={draft.payrollStartDate} onChange={(e) => set("payrollStartDate", e.target.value)} className={cn(field, focusRing)} />
        </label>
        <label className="block text-meta font-semibold">Payroll per run (blank = live average)
          <input type="number" min={0} step={100} inputMode="decimal" value={draft.payrollPerRun ?? ""} placeholder="Live average" onChange={(e) => set("payrollPerRun", e.target.value.trim() === "" ? null : num(e.target.value))} className={cn(field, focusRing)} />
        </label>
        <label className="block text-meta font-semibold">Average sales and costs over (months)
          <input type="number" min={1} max={12} step={1} value={draft.averagingMonths} onChange={(e) => set("averagingMonths", Math.round(num(e.target.value)))} className={cn(field, focusRing)} />
        </label>
      </fieldset>

      <h3 className="mt-6 text-label font-semibold">January</h3>
      <fieldset disabled={!edit} className="mt-2 grid gap-4 md:grid-cols-3">
        <legend className="sr-only">January bonus and year-end taxes</legend>
        <label className="block text-meta font-semibold">Kat's January bonus
          <input type="number" min={0} step={500} inputMode="decimal" value={draft.januaryBonus} onChange={(e) => set("januaryBonus", num(e.target.value))} className={cn(field, focusRing)} />
        </label>
        <label className="block text-meta font-semibold">Bonus paid on (blank = first January payday)
          <input type="date" value={draft.januaryBonusDate ?? ""} onChange={(e) => set("januaryBonusDate", e.target.value || null)} className={cn(field, focusRing)} />
        </label>
        <label className="block text-meta font-semibold">Employer payroll tax on it (%)
          <input type="number" min={0} max={20} step={0.05} value={draft.employerTaxPct} onChange={(e) => set("employerTaxPct", num(e.target.value))} className={cn(field, focusRing)} />
        </label>
        <label className="block text-meta font-semibold">Year-end taxes (0 = not set)
          <input type="number" min={0} step={500} inputMode="decimal" value={draft.yearEndTaxes} onChange={(e) => set("yearEndTaxes", num(e.target.value))} className={cn(field, focusRing)} />
        </label>
        <label className="block text-meta font-semibold">Taxes paid on (blank = January 15)
          <input type="date" value={draft.yearEndTaxesDate ?? ""} onChange={(e) => set("yearEndTaxesDate", e.target.value || null)} className={cn(field, focusRing)} />
        </label>
      </fieldset>

      <h3 className="mt-6 text-label font-semibold">Recurring bills</h3>
      <p className="text-meta text-ink-muted">Put a bill on its real dates. "Already in overhead" takes it out of the averaged overhead so it isn't counted twice; untick it for a new cost.</p>
      <div className="mt-2 space-y-2">
        {draft.recurringBills.map((b, i) => {
          const upd = (patch: Partial<typeof b>) => set("recurringBills", draft.recurringBills.map((x, j) => (j === i ? { ...x, ...patch } : x)));
          return (
            <fieldset key={b.id} disabled={!edit} className="grid items-end gap-2 md:grid-cols-[2fr_1fr_1fr_1fr_auto_auto]">
              <legend className="sr-only">Bill {i + 1}</legend>
              <label className="block text-meta">Name<input value={b.name} maxLength={80} onChange={(e) => upd({ name: e.target.value })} className={cn(field, focusRing)} /></label>
              <label className="block text-meta">Amount<input type="number" min={0} inputMode="decimal" value={b.amount} onChange={(e) => upd({ amount: num(e.target.value) })} className={cn(field, focusRing)} /></label>
              <label className="block text-meta">How often
                <select value={b.cadence} onChange={(e) => upd({ cadence: e.target.value as BillCadence })} className={cn(field, "min-h-[40px]", focusRing)}>{BILL_CADENCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
              </label>
              <label className="block text-meta">Next paid<input type="date" value={b.nextDate} onChange={(e) => upd({ nextDate: e.target.value })} className={cn(field, focusRing)} /></label>
              <label className="flex min-h-[40px] items-center gap-2 text-meta"><input type="checkbox" checked={b.countedInOverhead} onChange={(e) => upd({ countedInOverhead: e.target.checked })} /> Already in overhead</label>
              {edit ? <button type="button" aria-label={`Remove ${b.name || "bill"}`} onClick={() => set("recurringBills", draft.recurringBills.filter((_, j) => j !== i))} className={cn("btn-secondary min-h-[40px]", focusRing)}><Trash2 className="h-4 w-4" aria-hidden /></button> : null}
            </fieldset>
          );
        })}
        {!draft.recurringBills.length ? <p className="text-meta text-ink-muted">None.</p> : null}
        {edit ? <button type="button" className="btn-secondary inline-flex items-center gap-1.5" onClick={() => set("recurringBills", [...draft.recurringBills, { id: newId(), name: "", amount: 0, cadence: "monthly", nextDate: weeks[0] ?? "", countedInOverhead: true }])}><Plus className="h-4 w-4" aria-hidden /> Add a bill</button> : null}
      </div>

      <h3 className="mt-6 text-label font-semibold">Expected collections</h3>
      <p className="text-meta text-ink-muted">When you know a check is coming (or isn't), set that week's collections yourself. "Replace" swaps the computed figure for yours; "Add" puts yours on top.</p>
      <div className="mt-2 space-y-2">
        {draft.collectionOverrides.map((o, i) => {
          const upd = (patch: Partial<typeof o>) => set("collectionOverrides", draft.collectionOverrides.map((x, j) => (j === i ? { ...x, ...patch } : x)));
          const options = weeks.includes(o.weekStart) ? weeks : [o.weekStart, ...weeks];
          return (
            <fieldset key={o.id} disabled={!edit} className="grid items-end gap-2 md:grid-cols-[1fr_1fr_1fr_2fr_auto]">
              <legend className="sr-only">Collections override {i + 1}</legend>
              <label className="block text-meta">Week of
                <select value={o.weekStart} onChange={(e) => upd({ weekStart: e.target.value })} className={cn(field, "min-h-[40px]", focusRing)}>{options.map((w) => <option key={w} value={w}>{shortDate(w)}{weeks.includes(w) ? "" : " (past)"}</option>)}</select>
              </label>
              <label className="block text-meta">Replace or add
                <select value={o.mode} onChange={(e) => upd({ mode: e.target.value as "replace" | "add" })} className={cn(field, "min-h-[40px]", focusRing)}><option value="replace">Replace</option><option value="add">Add</option></select>
              </label>
              <label className="block text-meta">Amount<input type="number" min={0} inputMode="decimal" value={o.amount} onChange={(e) => upd({ amount: num(e.target.value) })} className={cn(field, focusRing)} /></label>
              <label className="block text-meta">Why<input value={o.note} maxLength={200} placeholder="e.g. Bozzuto check expected" onChange={(e) => upd({ note: e.target.value })} className={cn(field, focusRing)} /></label>
              {edit ? <button type="button" aria-label="Remove this override" onClick={() => set("collectionOverrides", draft.collectionOverrides.filter((_, j) => j !== i))} className={cn("btn-secondary min-h-[40px]", focusRing)}><Trash2 className="h-4 w-4" aria-hidden /></button> : null}
            </fieldset>
          );
        })}
        {!draft.collectionOverrides.length ? <p className="text-meta text-ink-muted">None — collections come from what's owed and average sales.</p> : null}
        {edit ? (
          <button type="button" className="btn-secondary inline-flex items-center gap-1.5" onClick={() => {
            const free = weeks.find((w) => !draft.collectionOverrides.some((o) => o.weekStart === w)) ?? weeks[0] ?? "";
            set("collectionOverrides", [...draft.collectionOverrides, { id: newId(), weekStart: free, amount: 0, mode: "replace", note: "" }]);
          }}><Plus className="h-4 w-4" aria-hidden /> Add a week</button>
        ) : null}
      </div>

      {edit ? (
        <div className="mt-6 flex flex-wrap items-end gap-3 border-t border-rule pt-4">
          <label className="block min-w-[260px] flex-1 text-meta font-semibold">What changed and why (optional)
            <input value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} className={cn(field, focusRing)} />
          </label>
          <button type="button" className="btn-primary" disabled={busy || !dirty} onClick={() => void save()}>{busy ? "Saving…" : "Save assumptions"}</button>
          <button type="button" className="btn-secondary" disabled={busy || !dirty} onClick={() => { setDraft(data.current.assumptions); setError(null); }}>Undo edits</button>
          <button type="button" className="btn-secondary" disabled={busy || JSON.stringify(draft) === JSON.stringify(data.defaults)} onClick={() => setDraft(data.defaults)}>Back to defaults</button>
        </div>
      ) : null}
      {error ? <p role="alert" className="mt-3 text-label text-risk">{error}</p> : null}
      {saved ? <p role="status" className="mt-3 text-label text-ok">{saved}</p> : null}

      <details className="mt-6">
        <summary className="cursor-pointer text-label font-semibold">History ({data.history.length})</summary>
        {data.history.length ? (
          <ol className="mt-2 space-y-3">
            {data.history.map((h) => (
              <li key={h.id} className="text-meta">
                <span className="font-semibold">{h.savedByName ?? "Someone"}</span> <span className="text-ink-muted">· {new Date(h.savedAt).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}</span>
                {h.note ? <span className="block">“{h.note}”</span> : null}
                {h.changes.map((c) => <span key={c} className="block text-ink-muted">{c}</span>)}
              </li>
            ))}
          </ol>
        ) : <p className="mt-2 text-meta text-ink-muted">No saves yet. Defaults: {money(data.defaults.ownerDrawMonthly)} draw at month end; payroll every two weeks on Fridays counted from {shortDate(data.defaults.payrollStartDate)}.</p>}
      </details>
    </Panel>
  );
}
